import { useTravelClaimFilesQuery, useTravelClaimQuery } from '../../hooks/useTravelClaimQuery';
import { Loading } from '../Loading';
import { useNavigate, useParams } from 'react-router-dom';
import { v4 as uuidV4 } from 'uuid';
import {
    DirectionModel,
    TravelClaimComplex,
    TravelClaimFileInList,
    TravelClaimModel,
    TravelClaimSimple,
    TravelClaimVise
} from '../../types/travelClaims';
import React, { useEffect, useState } from 'react';
import {
    ClaimBreadcrumbs,
    ClaimEditHeader,
    ClaimHeader,
    ComplexClaimStatus,
    EditHeader,
    SimpleClaimStatus,
    VisaStatus,
} from "./itemComponents/Headers";
import { AddDirection, DirectionItem, EditDirection } from "./itemComponents/Directions";
import { Attachments, AttachmentsEdit } from "./itemComponents/Attachments";
import { ButtonsGroup, ConfirmButton } from "./itemComponents/ItemActionButtons";
import {
    ComplexClaimDetails,
    ComplexClaimDetailsEdit,
    ComplexClaimHotelNeededEdit,
    ComplexClaimWishlist,
    ComplexClaimWishlistEdit,
    VisaComments,
    VisaCommentsEdit,
    VisaCountryEdit,
    VisaDepartureDate,
    VisaDepartureDateEdit,
    VisaEntryDate,
    VisaEntryDateEdit,
} from "./itemComponents/ClaimData";
import { Colleagues } from "./itemComponents/Colleagues";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { updateSimpleClaim, updateTravelClaim } from "../../services/travelClaims";
import { useTravelClaimModel } from "../../context/travelClaims";
import { Country } from "../../types/geo";
import VectorRight from "../../assets/vector_right.svg";
import TriangleLeft from "../../assets/triangle_left.svg";
import VectorDash from "../../assets/vector_dash.svg";
import { Direction } from "../../types/directions";
import { WebsocketMessageData, WebsocketMessagesTypes } from "../../types/websockets";
import { CommonWebsocket } from "../CommonWebsocket";
import { UserWebsocket } from "../UserWebsocket";
import { ChatItem } from "../chat/ChatItem";
import EmptyPlaceholder from "../_layout/EmptyPlaceholder";
import { formatDateToYYYYMMDD } from "../../helpers/dates";
import { isSafari } from "../../helpers/browsers"
import { eventEmitter, EventMap } from "../../helpers/eventEmitter";

type ClaimItemProps = {
    claim: TravelClaimSimple | TravelClaimComplex | TravelClaimVise | any
    files: TravelClaimFileInList[]
}

export const TravelClaimItem = () => {
    let { pk } = useParams();
    const queryClient = useQueryClient();

    const {
        data: claim,
        isLoading,
        isSuccess,
        refetch
    } = useTravelClaimQuery(pk || '');

    const {
        data: files,
        isLoading: isFilesLoading,
        isSuccess: isFilesSuccess
    } = useTravelClaimFilesQuery(pk);

    // Обновление списка файлов
    const handleRefreshFiles = () => {
        refetch();
    };

    const notDeletedFiles = () => {
        return files?.results.filter((file) => file.deleted_at == null) ?? []
    }

    // WS START vvv
    const [wsMessage, setWsMessage] = useState<WebsocketMessageData>();
    // Для установки непрочитанных сообщений
    const setClaimUnreadCount = (claim_pk: any, count: number) => {
        // обновляем и открытую заявку и ВСЕ кэши списка чтобы при возврате назад число было верным
        queryClient.setQueryData(['travelClaim'], (oldData: any) =>
            oldData && String(oldData.pk) === String(claim_pk) ? { ...oldData, unread_messages: count } : oldData
        );
        queryClient.setQueriesData({ queryKey: ['travelClaims'] }, (oldData: any) => {
            if (!oldData?.results) return oldData;
            return {
                ...oldData,
                results: oldData.results.map((item: any) =>
                    String(item.pk) === String(claim_pk) ? { ...item, unread_messages: count } : item
                ),
            };
        });
    };

    // Для индкатора непрочитанных уведомлений
    const invalidateUnread = () => {
        queryClient.invalidateQueries({
            queryKey: ['unreadNotifications'],
        });
    }

    // Для списка уведомлений
    const invalidateNotifications = () => {
        queryClient.invalidateQueries({
            queryKey: ['notifications'],
        });
    }

    // Обновление кол-ва непрочитанных в заявке
    useEffect(() => {
        switch (wsMessage?.msg_type) {
            case WebsocketMessagesTypes.NOTIFICATIONS_UPDATE:
                invalidateUnread();
                invalidateNotifications();
                break;
            case WebsocketMessagesTypes.CLAIM_UNREAD_COUNT: // точный счётчик персонально с бэка
                if (wsMessage.data?.claim_pk != null) {
                    setClaimUnreadCount(wsMessage.data.claim_pk, +wsMessage.data.unread_messages)
                }
                break;
            case WebsocketMessagesTypes.CLAIM_UPDATE:
                break;
            default:
                break;
        }
    }, [wsMessage]);
    // WS END ^^^

    useEffect(() => {
        const handleDocumentAction = (payload: EventMap['document']) => {
            if (payload.claimPk.toString() === pk) {
                switch (payload.action) {
                    case 'documentPreviewIsReady':
                    case 'newMediaMessage':
                        // Возможно медиа файл в чате был из вложений поэтому обновить список
                        handleRefreshFiles()
                        break;
                }
            }
        };

        eventEmitter.on('document', handleDocumentAction);

        return () => {
            eventEmitter.off('document', handleDocumentAction);
        };
    }, [pk]);

    if (isLoading || isFilesLoading) return <Loading />;
    if (!pk) return <Loading />;

    if (isSuccess && isFilesSuccess) {
        return (
            <div className='w-full flex flex-col gap-5 rounded-b-lg overflow-hidden'>
                {/*ОБРАБОТЧИК СОКЕТОВ ОБЩЕГО КАНАЛА*/}
                <CommonWebsocket
                    setWsMessageHandler={setWsMessage}
                ></CommonWebsocket>
                {/*ПЕРСОНАЛЬНЫЙ КАНАЛ СЧЁТЧИК НЕПРОЧИТАННЫХ*/}
                <UserWebsocket
                    setWsMessageHandler={setWsMessage}
                ></UserWebsocket>
                {claim.type === 'SIMPLE' &&
                    <SimpleClaimItem
                        claim={claim}
                        files={notDeletedFiles()}
                    />
                }
                {claim.type === 'COMPLEX' &&
                    <ComplexClaimItem
                        claim={claim}
                        files={notDeletedFiles()}
                    />
                }
                {claim.type === 'VISE' &&
                    <VisaItem
                        claim={claim}
                        files={notDeletedFiles()}
                    />
                }
            </div>
        )
    }
    return <EmptyPlaceholder text={'Данные не загружены'} />
};

export const TravelClaimItemEdit = () => {
    let { pk } = useParams();

    const {
        data: claim,
        isLoading,
        isSuccess
    } = useTravelClaimQuery(pk || '');

    const {
        data: files,
        isLoading: isFilesLoading,
        isSuccess: isFilesSuccess
    } = useTravelClaimFilesQuery(pk);

    const notDeletedFiles = () => {
        return files?.results.filter((file) => file.deleted_at == null) ?? []
    }

    if (isLoading || isFilesLoading) return <Loading />;
    if (!pk) return <Loading />;

    if (isSuccess && isFilesSuccess) {
        return (
            <div className='w-full flex flex-col gap-5 rounded-b-lg overflow-hidden'>
                {claim.type === 'SIMPLE' &&
                    <SimpleClaimItemEdit
                        claim={claim}
                        files={notDeletedFiles()}
                    />
                }
                {claim.type === 'COMPLEX' &&
                    <ComplexClaimItemEdit
                        claim={claim}
                        files={notDeletedFiles()}
                    />
                }
                {claim.type === 'VISE' &&
                    <VisaItemEdit
                        claim={claim}
                        files={notDeletedFiles()}
                    />}
            </div>
        )
    }

    return <EmptyPlaceholder text={'Пусто'} />
};

export const SimpleClaimItem = ({ claim, files }: ClaimItemProps) => {

    return (
        <div className='flex flex-col justify-between mt-2 gap-2'>
            {/* BREADCRUMBS */}
            <ClaimBreadcrumbs claimType={claim.type} />
            {/* CONTAINER */}
            <div className="flex flex-row justify-start gap-5">
                <div
                    className="flex flex-col w-5/12 h-auto max-h-[80vh] bg-white rounded-lg justify-start p-4"
                    style={{ overflow: isSafari() ? 'visible' : 'hidden' }}
                >
                    <ClaimHeader pk={claim.pk} author={claim.author} created_at={claim.created_at} />
                    {/*SCROLL CONTAINER*/}
                    <div className="inline-flex flex-col w-full h-full min-h-72 justify-start overflow-y-auto">
                        {/*HEADERS*/}
                        <SimpleClaimStatus
                            status={claim.status}
                            service={claim.service}
                            direction_name={claim.direction_name}
                            started_at={claim.claim_started_at}
                            finished_at={claim.claim_finished_at}
                        />
                        {/*DIRECTIONS*/}
                        <div className="w-full inline-flex p-5 flex-col items-center gap-2.5 relative flex-[0_0_auto]">
                            {claim.directions.map((direction: Direction, index: number) => (
                                <DirectionItem key={index} direction={direction} index={index} />
                            ))}
                        </div>
                        {/*ATTACHMENTS*/}
                        <Attachments list={files} />
                        {/*COLLEAGUES*/}
                        <Colleagues claim_pk={claim.pk} colleagues={claim.colleagues} outstaff={claim.outstaff} />
                    </div>
                    {/*BUTTONS*/}
                    <ButtonsGroup
                        claimId={claim.pk}
                        claimStatus={claim.status}
                    />
                </div>
                <ChatItem claim={claim} />
            </div>
        </div>
    );
}

export const SimpleClaimItemEdit = ({ claim, files }: ClaimItemProps) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    // Модель и методы для изменений
    const {
        model,
        updateModel,
        updateNestedModel,
        addNestedModel
    } = useTravelClaimModel();

    // Запросы на изменение простой заявки
    const { mutate: update, isPending } = useMutation({
        mutationFn: ({ pk, model }: {
            pk: string,
            model: TravelClaimModel
        }) => updateSimpleClaim(pk, model.directions),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaim', claim.pk.toString()] })
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        },
        onSettled: () => {
            // Заново обновляем состояние модели редактирования
            updateModel({
                directions: claim.directions.map((e: DirectionModel) => {
                    e.id = e.pk || '';
                    return e
                })
            })
            navigate(-1); // Переход на 1 станицу назад
        }
    });

    // Заполнение модели изменяемых полей в заявке
    useEffect(() => {
        updateModel({
            directions: claim.directions.map((e: DirectionModel) => {
                e.id = e.pk || '';
                return e
            })
        })
    }, [claim]);

    // Добавление вложенной модели в массив
    const handleAddNestedModel = () => {
        const newId = uuidV4();
        addNestedModel({
            id: newId,
            pk: null,
            ticket_type: 'ANY',
        });
    };

    // Сохранение модели
    const handleSave = (pk: string) => {
        update({ pk, model })
    }

    return (
        <div className='flex flex-col justify-between mt-2 gap-2'>
            {/* BREADCRUMBS */}
            <ClaimBreadcrumbs claimType={claim.type} />
            {/* CONTAINER */}
            <div className="flex flex-row justify-start gap-5">
                <div
                    className="w-5/12 h-auto max-h-[80vh] flex flex-col justify-start items-center bg-white rounded-lg">
                    {/* EDIT HEADER */}
                    <EditHeader />
                    {/*HEADER*/}
                    <ClaimEditHeader pk={claim.pk} author={claim.author} created_at={claim.created_at} />
                    {/*SCROLL CONTAINER */}
                    <div className="w-full h-full min-h-72 flex flex-col justify-start overflow-y-auto">
                        {/*DIRECTIONS*/}
                        <AddDirection onClick={handleAddNestedModel} />
                        <EditDirection />
                        {/*ATTACHMENTS*/}
                        <AttachmentsEdit claim_pk={claim.pk} list={files} />

                        {/*COLLEAGUES*/}
                        <Colleagues claim_pk={claim.pk} colleagues={claim.colleagues} outstaff={claim.outstaff} />
                    </div>
                    {/*BUTTONS*/}
                    <ConfirmButton
                        isLoading={isPending}
                        isDisabled={false}
                        onClick={handleSave}
                        claimId={claim.pk}
                        claimType={claim.type}
                    />
                </div>
                <ChatItem claim={claim} />
            </div>
        </div>
    );
};

export const ComplexClaimItem = ({ claim, files }: ClaimItemProps) => {
    return (
        <div className='flex flex-col justify-between mt-2 gap-2'>
            {/* BREADCRUMBS */}
            <ClaimBreadcrumbs claimType={claim.type} />
            {/* CONTAINER */}
            <div className="flex flex-row justify-start gap-5">
                <div
                    className="flex flex-col w-5/12 h-auto max-h-[80vh] bg-white rounded-lg justify-start"
                    style={{ overflow: isSafari() ? 'visible' : 'hidden' }}>
                    <ClaimHeader pk={claim.pk} author={claim.author} created_at={claim.created_at} />
                    {/*SCROLL CONTAINER*/}
                    <div className="inline-flex flex-col w-full h-full min-h-72 justify-start overflow-y-auto">
                        {/*HEADERS*/}
                        <ComplexClaimStatus
                            status={claim.status}
                            name={claim.name}
                            service={claim.service}
                        />
                        {/*DETAILS*/}
                        <ComplexClaimDetails details={claim.details} />
                        <ComplexClaimWishlist hotel_wishes={claim.hotel_wishes} />
                        {/*ATTACHMENTS */}
                        <Attachments
                            list={files}
                        />
                        {/*COLLEAGUES*/}
                        <Colleagues claim_pk={claim.pk} colleagues={claim.colleagues} outstaff={claim.outstaff} />

                    </div>
                    {/*BUTTONS*/}
                    <ButtonsGroup
                        claimId={claim.pk}
                        claimStatus={claim.status}
                    />
                </div>
                <ChatItem claim={claim} />
            </div>
        </div>
    );
}

export const ComplexClaimItemEdit = ({ claim, files }: ClaimItemProps) => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    // Модель изменений
    const { model, updateModel } = useTravelClaimModel();
    // Запрос на изменения
    const { mutate: update, isPending } = useMutation({
        mutationFn: ({ pk, model }: {
            pk: string,
            model: TravelClaimModel
        }) => updateTravelClaim(pk, model),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaim', claim.pk.toString()] })
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        },
        onSettled: () => {
            navigate(-1); // Переход на 1 станицу назад
        }
    });

    // Заполнение модели изменяемых полей в заявке
    useEffect(() => {
        updateModel({
            comment: claim.comment,
            details: claim.details,
            hotel_wishes: claim.hotel_wishes,
            without_hotel: claim.without_hotel,
        })
    }, [
        // updateModel
    ]);

    const isFormCorrected = () => {
        const isCorrectHotelData = model.without_hotel ? true : (model.hotel_wishes?.length ?? 0) > 0
        return model.details && model.details.length > 0 && isCorrectHotelData
    };

    // Сохранение модели
    const handleSave = (pk: string) => {
        update({ pk, model })
    }

    return (
        <div className='flex flex-col justify-between mt-2 gap-2'>
            {/* BREADCRUMBS */}
            <ClaimBreadcrumbs claimType={claim.type} />
            {/* CONTAINER */}
            <div className="flex flex-row justify-start gap-5">
                <div
                    className="w-5/12 h-auto max-h-[75vh] flex flex-col justify-start items-center bg-white rounded-lg">
                    {/* EDIT HEADER */}
                    <EditHeader />
                    {/* HEADER */}
                    <ClaimEditHeader pk={claim.pk} author={claim.author} created_at={claim.created_at} />
                    {/*SCROLL CONTAINER */}
                    <div
                        className="
                        w-full h-full
                        min-h-72
                        flex flex-col
                        justify-start
                        overflow-y-auto
                        "
                    >
                        {/*DETAILS*/}
                        <ComplexClaimDetailsEdit
                            onChange={(details: string) => updateModel({ details })}
                            details={claim.details}
                        />
                        {/*HOTEL*/}
                        <div
                            className="px-5 inline-flex flex-col w-full mb-5 items-start gap-5 relative flex-[0_0_auto]">
                            <div className="flex w-full items-center gap-5 relative flex-[0_0_auto]">
                                <div
                                    className="w-full text-black text-[length:var(--manager-acrom-h3-20-medium-acrom-font-size)] leading-[var(--manager-acrom-h3-20-medium-acrom-line-height)] relative font-manager-acrom-h3-20-medium-acrom font-[number:var(--manager-acrom-h3-20-medium-acrom-font-weight)] tracking-[var(--manager-acrom-h3-20-medium-acrom-letter-spacing)] [font-style:var(--manager-acrom-h3-20-medium-acrom-font-style)]">
                                    Отель
                                </div>
                            </div>
                            <div className="inline-flex flex-col w-full items-start gap-5 relative flex-[0_0_auto]">
                                <ComplexClaimWishlistEdit
                                    onChange={(hotel_wishes: string) => updateModel({ hotel_wishes })}
                                    hotel_wishes={claim.hotel_wishes}
                                    without_hotel={model.without_hotel}
                                />
                                <ComplexClaimHotelNeededEdit
                                    onChange={(without_hotel: boolean) => updateModel({ without_hotel })}
                                    without_hotel={claim.without_hotel} />
                            </div>
                        </div>
                        {/*ATTACHMENTS*/}
                        <AttachmentsEdit claim_pk={claim.pk} list={files} />
                        {/*COLLEAGUES*/}
                        <Colleagues claim_pk={claim.pk} colleagues={claim.colleagues} outstaff={claim.outstaff} />

                    </div>
                    {/*CONFIRM BUTTON*/}
                    <ConfirmButton
                        onClick={handleSave}
                        isLoading={isPending}
                        isDisabled={!isFormCorrected()}
                        claimId={claim.pk}
                        claimType={claim.type}
                    />
                </div>
                <ChatItem claim={claim} />
            </div>
        </div>
    );
}

export const VisaItem = ({ claim, files }: ClaimItemProps) => {
    return (
        <div className='flex flex-col justify-between mt-2 gap-2'>
            {/* BREADCRUMBS */}
            <ClaimBreadcrumbs claimType={claim.type} />
            {/* CONTAINER */}
            <div className="flex flex-row justify-start gap-5">
                <div
                    className="flex flex-col w-5/12 h-auto max-h-[75vh] bg-white rounded-lg overflow-hidden justify-start"
                >
                    <ClaimHeader pk={claim.pk} author={claim.author} created_at={claim.created_at} />
                    {/*SCROLL CONTAINER*/}
                    <div className="inline-flex flex-col w-full h-full min-h-72 justify-start overflow-y-auto">
                        {/*HEADERS*/}
                        <VisaStatus country={claim.country} status={claim.status} service={claim.service} />
                        {/*DATES*/}
                        <div
                            className="m-5 inline-flex flex-col items-start gap-10 p-5 relative flex-[0_0_auto] bg-white rounded-lg overflow-hidden shadow-shadow-card">
                            <div className="w-full flex flex-row justify-around items-center h-10">
                                <VisaEntryDate entry_date={claim.entry_date} />
                                <div
                                    className="w-2/4 h-1.5 gap-5 flex flex-row justify-center items-center align-middle">
                                    <div className="start1 w-1/2 flex flex-row justify-center items-center">
                                        <div className="w-1.5 h-1.5 bg-black rounded-[3px]" />
                                        <img
                                            alt=""
                                            src={VectorRight}
                                            className="w-full !h-1.5 !object-cover" />
                                    </div>
                                    <div className="w-1/2 flex flex-row justify-center items-center">
                                        <img alt="" src={TriangleLeft} className=" " />
                                        <img alt="" src={VectorDash}
                                            className="w-full !object-cover" />
                                        <div
                                            className=" w-1.5 h-1.5  bg-white rounded-[3px] border border-solid border-[#282828]" />
                                    </div>
                                </div>
                                <VisaDepartureDate departure_date={claim.departure_date} />
                            </div>
                        </div>
                        {/*COMMENTS*/}
                        <VisaComments comment={claim.comment} />
                        {/*ATTACHMENTS*/}
                        <Attachments list={files} />
                        {/*COLLEAGUES*/}
                        <Colleagues claim_pk={claim.pk} colleagues={claim.colleagues} outstaff={claim.outstaff} />
                    </div>
                    {/*BUTTONS*/}
                    <ButtonsGroup
                        claimId={claim.pk}
                        claimStatus={claim.status}
                    />
                </div>
                <ChatItem claim={claim} />
            </div>
        </div>
    );
}

export const VisaItemEdit = ({ claim, files }: ClaimItemProps) => {
    const navigate = useNavigate()
    const queryClient = useQueryClient();
    // Модель изменений
    const { model, updateModel } = useTravelClaimModel();
    // Запрос на изменения
    const { mutate: update, isPending } = useMutation({
        mutationFn: ({ pk, model }: {
            pk: string,
            model: TravelClaimModel
        }) => updateTravelClaim(pk, model),
        onSuccess: (data) => {
            queryClient.invalidateQueries({ queryKey: ['travelClaim', claim.pk.toString()] })
            queryClient.invalidateQueries({ queryKey: ['travelClaims'] })
        },
        onSettled: () => {
            navigate(-1); // Переход на 1 станицу назад
        }
    });

    // Заполнение модели изменяемых полей в заявке
    useEffect(() => {
        updateModel({
            comment: claim.comment,
            entry_date: claim.entry_date,
            departure_date: claim.departure_date,
            details: claim.details,
            hotel_wishes: claim.hotel_wishes,
            country: claim.country
        })
    }, [
        // updateModel
    ]);

    // Управление полями //
    const setCountry = (country: Country) => {
        updateModel({ country });
    }
    const setComment = (comment: string) => {
        updateModel({ comment });
    }
    const setEntryDate = (date: string) => {
        let resultDate = date;
        if (date && date.length) {
            // Дата начала не может быть меньше текущей даты
            const nowDate = new Date();
            const currentDate = new Date(date);
            if (currentDate < nowDate) {
                resultDate = formatDateToYYYYMMDD(nowDate)
            }
            // Если ранее была установлена дата окончания и она меньше выбранной даты начала
            if (model.departure_date) {
                const departureDate = new Date(model.departure_date);
                if (currentDate > departureDate) {
                    updateModel({ departure_date: resultDate })
                }

            }
        }
        updateModel({ entry_date: resultDate });
    }
    const setDepartureDate = (date: string) => {
        let resultDate = date;
        if (date && date.length) {
            // Дата окончания не может быть меньше текущей даты и даты начала
            const nowDate = new Date();
            const currentDate = new Date(date);
            if (currentDate < nowDate) {
                resultDate = formatDateToYYYYMMDD(nowDate)
            }
            // Если ранее была установлена дата начала и она выбранная дата окончания меньше даты начала
            if (model.entry_date) {
                const entry_date = new Date(model.entry_date);
                if (currentDate < entry_date) {
                    resultDate = formatDateToYYYYMMDD(entry_date)
                }

            }
        }
        updateModel({ departure_date: resultDate });
    }

    // Сохранение модели
    const handleSave = (pk: string) => {
        update({ pk, model })
    }

    return (
        <div className='flex flex-col justify-between mt-2 gap-2'>
            {/* BREADCRUMBS */}
            <ClaimBreadcrumbs claimType={claim.type} />
            {/* CONTAINER */}
            <div className="flex flex-row justify-start gap-5">
                <div
                    className="w-5/12 h-auto max-h-[75vh] flex flex-col justify-start items-center bg-white rounded-lg"
                >
                    {/* EDIT HEADER */}
                    <EditHeader />
                    {/*HEADER*/}
                    <ClaimEditHeader pk={claim.pk} author={claim.author} created_at={claim.created_at} />
                    {/*SCROLL CONTAINER */}
                    <div className="w-full h-full min-h-72 flex flex-col justify-start overflow-y-auto">
                        {/*Country*/}
                        <VisaCountryEdit
                            onChange={setCountry}
                            country={claim.country}
                        />
                        {/*DATES*/}
                        <div className="w-full inline-flex mb-5 justify-between items-start flex-[0_0_auto]">
                            <VisaEntryDateEdit
                                onChange={setEntryDate}
                                entry_date={model.entry_date}
                            />
                            <VisaDepartureDateEdit
                                onChange={setDepartureDate}
                                departure_date={model.departure_date}
                            />
                        </div>
                        {/*COMMENT*/}
                        <VisaCommentsEdit
                            onChange={setComment}
                            comment={claim.comment}
                        />
                        {/*ATTACHMENTS*/}
                        <AttachmentsEdit claim_pk={claim.pk} list={files} />
                        {/*<FileUploadList claim_pk={claim.pk}/>*/}

                        {/*COLLEAGUES*/}
                        <Colleagues claim_pk={claim.pk} colleagues={claim.colleagues} outstaff={claim.outstaff} />
                    </div>
                    {/*BUTTONS*/}
                    <ConfirmButton
                        isLoading={isPending}
                        isDisabled={false}
                        onClick={handleSave}
                        claimId={claim.pk}
                        claimType={claim.type}
                    />
                </div>
                <ChatItem claim={claim} />
            </div>
        </div>
    );
}
